/**
 * Wallstraight anniversary celebration popup.
 *
 * Behavior contract (see snippets/ws-anniversary-modal.liquid for the
 * markup contract and config/settings_schema.json for the merchant
 * controls):
 *
 *   • Fires once per browser session after the page becomes
 *     interactive. Uses requestIdleCallback with a 2.5s safety timer
 *     so the popup never competes for the LCP image budget.
 *   • Suppresses for N days after dismissal (N = window.WS_ANNIV_MODAL
 *     .suppressDays, populated by the snippet from the theme setting).
 *   • Skips on /cart and /checkout URLs and while any other primary
 *     modal is open (welcomeOfferModal, retryModal, petNameModal,
 *     studioRecoveryModal, or the funnel loading screen #ld).
 *   • CTA navigates normally — link click counts as engaged dismiss,
 *     so the popup will not reappear during the suppression window.
 *   • When no separate CTA button is rendered (CTA baked into the
 *     PNG), card clicks navigate to ctaUrl except the top-right close
 *     zone, which dismisses. Backdrop clicks always dismiss.
 *
 * Language-aware artwork swap is delegated to assets/ws-promo-i18n.js
 * via the shared data-ws-promo-bilingual / data-ws-promo-lang hooks.
 *
 * Storage keys (kept distinct from the welcome-offer keys so the two
 * modals can coexist):
 *   sessionStorage  ws_anniv_modal_shown        : '1' after first open this tab
 *   localStorage    ws_anniv_modal_dismissed_at : ms timestamp of last dismiss
 */
(function () {
  'use strict';

  var SESSION_KEY = 'ws_anniv_modal_shown';
  var DISMISSED_AT_KEY = 'ws_anniv_modal_dismissed_at';
  var SHOW_DELAY_MS = 2500;
  var IDLE_TIMEOUT_MS = 4000;
  var OTHER_MODAL_IDS = [
    'welcomeOfferModal',
    'retryModal',
    'petNameModal',
    'studioRecoveryModal',
    'ld'
  ];

  var cfg =
    typeof window.WS_ANNIV_MODAL === 'object' && window.WS_ANNIV_MODAL
      ? window.WS_ANNIV_MODAL
      : { suppressDays: 3, skipPathPrefixes: ['/cart', '/checkout'] };

  var modal = null;
  var lastFocus = null;
  var armed = false;

  function safeGetSession(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function safeSetSession(key, val) {
    try {
      sessionStorage.setItem(key, val);
    } catch (_) {}
  }

  function safeGetLocal(key) {
    try {
      return localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function safeSetLocal(key, val) {
    try {
      localStorage.setItem(key, val);
    } catch (_) {}
  }

  function pathSkipped() {
    var p = (location && location.pathname) || '';
    var prefixes = Array.isArray(cfg.skipPathPrefixes) ? cfg.skipPathPrefixes : [];
    for (var i = 0; i < prefixes.length; i++) {
      if (p.indexOf(prefixes[i]) === 0) return true;
    }
    return false;
  }

  function otherModalBlocking() {
    for (var i = 0; i < OTHER_MODAL_IDS.length; i++) {
      var el = document.getElementById(OTHER_MODAL_IDS[i]);
      if (el && el.classList && el.classList.contains('show')) return true;
    }
    var res = document.getElementById('resPage');
    if (res && res.classList && res.classList.contains('show')) return true;
    return false;
  }

  function withinSuppressWindow() {
    var days = Number(cfg.suppressDays);
    if (!isFinite(days) || days <= 0) return false;
    var stampStr = safeGetLocal(DISMISSED_AT_KEY);
    var stamp = stampStr ? parseInt(stampStr, 10) || 0 : 0;
    if (!stamp) return false;
    return Date.now() - stamp < days * 86400000;
  }

  function shouldShow() {
    if (!modal) return false;
    if (safeGetSession(SESSION_KEY) === '1') return false;
    if (withinSuppressWindow()) return false;
    if (pathSkipped()) return false;
    if (otherModalBlocking()) return false;
    return true;
  }

  function trapFocus(e) {
    if (e.key !== 'Tab' || !modal) return;
    var nodes = modal.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      dismiss();
      return;
    }
    trapFocus(e);
  }

  function open() {
    if (!modal) return;
    if (!shouldShow()) return;
    safeSetSession(SESSION_KEY, '1');

    lastFocus = document.activeElement;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function () {
      modal.classList.add('is-open');
      try {
        if (typeof window.wsPromoI18nApply === 'function') window.wsPromoI18nApply();
      } catch (_) {}
      try {
        modal.focus({ preventScroll: true });
      } catch (_) {
        try {
          modal.focus();
        } catch (__) {}
      }
    });

    document.addEventListener('keydown', onKeyDown, true);
  }

  function dismiss() {
    if (!modal) return;
    safeSetLocal(DISMISSED_AT_KEY, String(Date.now()));
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');

    var finalize = function () {
      modal.hidden = true;
      document.removeEventListener('keydown', onKeyDown, true);
      var prev = lastFocus;
      lastFocus = null;
      if (prev && typeof prev.focus === 'function') {
        try { prev.focus(); } catch (_) {}
      }
    };

    var prefersReduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      finalize();
    } else {
      setTimeout(finalize, 220);
    }
  }

  function onCtaClick() {
    safeSetLocal(DISMISSED_AT_KEY, String(Date.now()));
  }

  /** Baked-in close control sits in the PNG's top-right corner (see modal artwork). */
  function isImageCloseZone(e, card) {
    var rect = card.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    var x = (e.clientX - rect.left) / rect.width;
    var y = (e.clientY - rect.top) / rect.height;
    return x >= 0.8 && y <= 0.18;
  }

  function navigateToCta() {
    onCtaClick();
    var url =
      cfg && typeof cfg.ctaUrl === 'string' && cfg.ctaUrl
        ? cfg.ctaUrl
        : '/pages/wallstraight-portraits';
    window.location.href = url;
  }

  function bind() {
    if (!modal) return;
    /** One handler for overlay, image, and card — works on click (Chrome, Safari, Android). */
    modal.addEventListener(
      'click',
      function (e) {
        if (!modal.classList.contains('is-open')) return;
        var cta =
          e.target && e.target.closest && e.target.closest('[data-ws-anniv-cta]');
        if (cta) {
          onCtaClick();
          return;
        }
        var card =
          e.target && e.target.closest && e.target.closest('.ws-anniv-modal__card');
        if (card) {
          var htmlCta = modal.querySelector('[data-ws-anniv-cta]');
          if (!htmlCta) {
            if (isImageCloseZone(e, card)) {
              dismiss();
            } else {
              navigateToCta();
            }
            return;
          }
        }
        dismiss();
      },
      false
    );
  }

  function arm() {
    if (armed) return;
    armed = true;
    modal = document.getElementById('wsAnnivModal');
    if (!modal) return;
    bind();
    if (!shouldShow()) return;
    var fire = function () {
      setTimeout(function () {
        if (shouldShow()) open();
      }, SHOW_DELAY_MS);
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(fire, { timeout: IDLE_TIMEOUT_MS });
    } else {
      setTimeout(fire, 200);
    }
  }

  if (document.readyState === 'complete') {
    arm();
  } else {
    window.addEventListener('load', arm, { once: true });
  }
})();
